import {
    POS_SIZING_RISK_SYNC_REQUIRED_CODE,
    POS_SIZING_RISK_SYNC_REQUIRED_MESSAGE,
} from '../../utils/posSizingRiskReadiness';

export const RISK_OVERVIEW_RESOURCE_STATES = Object.freeze({
    IDLE: 'idle',
    LOADING: 'loading',
    EMPTY: 'empty',
    PENDING: 'pending',
    PARTIAL: 'partial',
    READY: 'ready',
    ERROR: 'error',
});

const RESOURCE_META_FIELD = 'riskOverviewResource';
const DEFAULT_ERROR_CODE = 'risk_overview_unavailable';
const DEFAULT_SAFE_ERROR_MESSAGE = 'Risk data is unavailable.';
const FINANCIAL_PANEL_NAMES = new Set(['primary', 'group']);

const normalizeScopePart = (value) => {
    if (value == null) return '';
    return String(value).trim();
};

export function buildRiskOverviewScopeKey({
    userUid,
    portfolioId,
    portfolioRevision,
    targetKey,
    factorModel,
    factorDataModel,
    factorSnapshotDate,
    betaSource,
    idioVolSource,
} = {}) {
    return JSON.stringify([
        normalizeScopePart(userUid),
        normalizeScopePart(portfolioId),
        normalizeScopePart(portfolioRevision),
        normalizeScopePart(targetKey),
        normalizeScopePart(factorModel),
        normalizeScopePart(factorDataModel),
        normalizeScopePart(factorSnapshotDate),
        normalizeScopePart(betaSource),
        normalizeScopePart(idioVolSource),
    ]);
}

export function buildSelectedRiskOverviewScopeKey({
    userUid,
    portfolioId,
    portfolioRefreshToken,
    watchlistVersion,
    posSizingVersion,
    invalidationVersion,
    targetKey,
    riskContext = {},
} = {}) {
    return buildRiskOverviewScopeKey({
        userUid,
        portfolioId,
        portfolioRevision: JSON.stringify([
            portfolioRefreshToken ?? '',
            watchlistVersion,
            posSizingVersion,
            invalidationVersion,
        ]),
        targetKey,
        factorModel: riskContext?.factorModel,
        factorDataModel: riskContext?.factorDataModel,
        factorSnapshotDate: riskContext?.factorSnapshotDate,
        betaSource: riskContext?.betaSource,
        idioVolSource: riskContext?.idioVolSource,
    });
}

const normalizeSnapshotToken = (resource) => normalizeScopePart(
    resource?.risk_calculation_coverage?.snapshot_token,
);

export function createRiskOverviewSnapshotPublicationTransaction({
    requiredPanels,
    scopeKey,
    generation,
    isCurrent,
    expectedSnapshotToken = null,
} = {}) {
    if (typeof isCurrent !== 'function') {
        throw new Error('Risk Overview snapshot transaction requires a current-request fence.');
    }
    const panels = Array.isArray(requiredPanels)
        ? requiredPanels.map(normalizeScopePart).filter(Boolean)
        : [];
    if (panels.length === 0 || new Set(panels).size !== panels.length) {
        throw new Error('Risk Overview snapshot transaction requires unique named panels.');
    }
    const normalizedScopeKey = normalizeScopePart(scopeKey);
    if (
        !normalizedScopeKey
        || !Number.isInteger(generation)
        || generation < 0
    ) {
        throw new Error('Risk Overview snapshot transaction requires scope and generation.');
    }
    const expectedToken = normalizeScopePart(expectedSnapshotToken);
    const entries = new Map();
    let closed = false;

    const discard = () => {
        closed = true;
        entries.clear();
    };
    const getCommitPlan = () => {
        if (closed || !isCurrent()) return false;
        const complete = panels.every((panel) => {
            const entry = entries.get(panel);
            return (
                typeof entry?.publish === 'function'
                && typeof entry?.stillCurrent === 'function'
                && entry.stillCurrent()
                && entry.scopeKey === normalizedScopeKey
                && entry.generation === generation
            );
        });
        if (!complete) return false;

        const failedPanel = panels.find((panel) => (
            FINANCIAL_PANEL_NAMES.has(panel)
            && entries.get(panel)?.resource?.[RESOURCE_META_FIELD]?.state
                === RISK_OVERVIEW_RESOURCE_STATES.ERROR
        ));
        if (failedPanel) {
            return { kind: 'failure', entry: entries.get(failedPanel) };
        }

        const snapshotTokens = new Set(
            panels.map((panel) => normalizeSnapshotToken(entries.get(panel)?.resource)),
        );
        if (snapshotTokens.has('')) return false;
        if (
            snapshotTokens.size !== 1
            || (expectedToken && !snapshotTokens.has(expectedToken))
        ) {
            return { kind: 'snapshot_mismatch' };
        }
        return { kind: 'success' };
    };
    const canCommit = () => ['success', 'failure'].includes(getCommitPlan()?.kind);

    return {
        stage(publish, stillCurrent, metadata = {}) {
            const { panel, resource } = metadata;
            if (closed) return;
            if (!panels.includes(panel)) {
                throw new Error('Risk Overview snapshot publication requires an expected panel.');
            }
            if (entries.has(panel)) {
                throw new Error(`Risk Overview snapshot staged duplicate ${panel} panel.`);
            }
            entries.set(panel, {
                publish,
                stillCurrent,
                resource,
                scopeKey: normalizeScopePart(metadata.scopeKey),
                generation: metadata.generation,
            });
        },
        canCommit,
        hasSnapshotMismatch() {
            return getCommitPlan()?.kind === 'snapshot_mismatch';
        },
        willPublishFailure() {
            return getCommitPlan()?.kind === 'failure';
        },
        commit() {
            const plan = getCommitPlan();
            if (!plan || plan.kind === 'snapshot_mismatch') {
                discard();
                return false;
            }
            closed = true;
            if (plan.kind === 'failure') {
                plan.entry.publish();
            } else {
                panels.forEach((panel) => entries.get(panel).publish());
            }
            entries.clear();
            return true;
        },
        discard,
    };
}

const toCount = (value) => {
    const count = Number(value);
    return Number.isFinite(count) && count >= 0 ? count : 0;
};

export function deriveRiskOverviewResourceState(payload) {
    if (!payload || typeof payload !== 'object') {
        return RISK_OVERVIEW_RESOURCE_STATES.IDLE;
    }
    if (payload.success === false) {
        return RISK_OVERVIEW_RESOURCE_STATES.ERROR;
    }

    const status = normalizeScopePart(payload.status).toUpperCase();
    if (status === 'EMPTY_PORTFOLIO') {
        return RISK_OVERVIEW_RESOURCE_STATES.EMPTY;
    }
    if (status.startsWith('PENDING_')) {
        return RISK_OVERVIEW_RESOURCE_STATES.PENDING;
    }

    const coverage = payload.risk_calculation_coverage;
    if (coverage && typeof coverage === 'object') {
        const total = toCount(coverage.total_positions);
        const included = toCount(coverage.included_count);
        const pending = toCount(coverage.pending_count);
        const excluded = toCount(coverage.excluded_count);
        const failed = toCount(coverage.failed_count);
        if (pending > 0 && included === 0) {
            return RISK_OVERVIEW_RESOURCE_STATES.PENDING;
        }
        if (total > 0 && (pending + excluded + failed > 0 || included < total)) {
            return RISK_OVERVIEW_RESOURCE_STATES.PARTIAL;
        }
    }
    return RISK_OVERVIEW_RESOURCE_STATES.READY;
}

export function createRiskOverviewResource({
    scopeKey = '',
    generation = 0,
    state = RISK_OVERVIEW_RESOURCE_STATES.IDLE,
    data = null,
    coverage = null,
    errorCode = null,
    safeMessage = null,
    clientObservedAt = null,
} = {}) {
    const hasError = state === RISK_OVERVIEW_RESOURCE_STATES.ERROR;
    return {
        scopeKey: normalizeScopePart(scopeKey),
        generation: Number.isInteger(generation) && generation >= 0 ? generation : 0,
        state,
        data,
        coverage,
        error: hasError
            ? {
                code: normalizeScopePart(errorCode) || DEFAULT_ERROR_CODE,
                message: normalizeScopePart(safeMessage) || DEFAULT_SAFE_ERROR_MESSAGE,
            }
            : null,
        clientObservedAt,
    };
}

export function withRiskOverviewResource(payload, {
    scopeKey,
    generation,
    state,
    errorCode = null,
    safeMessage = null,
} = {}) {
    const data = payload && typeof payload === 'object' ? payload : {};
    const resolvedState = state ?? deriveRiskOverviewResourceState(data);
    const hasError = resolvedState === RISK_OVERVIEW_RESOURCE_STATES.ERROR;
    return {
        ...data,
        [RESOURCE_META_FIELD]: {
            scopeKey: normalizeScopePart(scopeKey),
            generation: Number.isInteger(generation) && generation >= 0 ? generation : 0,
            state: resolvedState,
            errorCode: hasError
                ? normalizeScopePart(errorCode) || DEFAULT_ERROR_CODE
                : null,
            safeMessage: hasError
                ? normalizeScopePart(safeMessage) || DEFAULT_SAFE_ERROR_MESSAGE
                : null,
        },
    };
}

export function readRiskOverviewResource(payload, {
    loading = false,
    clientObservedAt = null,
    emptyState = RISK_OVERVIEW_RESOURCE_STATES.IDLE,
    expectedScopeKey = null,
} = {}) {
    const candidate = payload && typeof payload === 'object' ? payload : null;
    const candidateMetadata = candidate?.[RESOURCE_META_FIELD] ?? null;
    const hasExpectedScope = expectedScopeKey != null;
    const scopeMatches = !hasExpectedScope
        || candidateMetadata?.scopeKey === normalizeScopePart(expectedScopeKey);
    const data = scopeMatches ? candidate : null;
    const metadata = scopeMatches ? candidateMetadata : null;
    const state = loading
        ? RISK_OVERVIEW_RESOURCE_STATES.LOADING
        : metadata?.state ?? (data ? deriveRiskOverviewResourceState(data) : emptyState);
    return createRiskOverviewResource({
        scopeKey: metadata?.scopeKey ?? '',
        generation: metadata?.generation ?? 0,
        state,
        data,
        coverage: data?.risk_calculation_coverage ?? null,
        errorCode: metadata?.errorCode ?? null,
        safeMessage: metadata?.safeMessage ?? null,
        clientObservedAt,
    });
}

export const buildUnavailableResources = ({
    scopeKey, generation, isSelected, posSizingSyncRequired = false,
}) => ({
    primary: withRiskOverviewResource({
        success: false,
        status: posSizingSyncRequired ? 'POSITION_SIZING_SYNC_REQUIRED' : 'RISK_OVERVIEW_UNAVAILABLE',
        ...(posSizingSyncRequired ? { action: 'sync_draft' } : {}),
        message: posSizingSyncRequired
            ? POS_SIZING_RISK_SYNC_REQUIRED_MESSAGE
            : (isSelected
                ? 'Selected portfolio risk is temporarily unavailable.'
                : 'Risk metrics are temporarily unavailable.'),
        holdingsCalculations: [],
        factorExposures: [],
    }, {
        scopeKey,
        generation,
        state: RISK_OVERVIEW_RESOURCE_STATES.ERROR,
        errorCode: posSizingSyncRequired
            ? POS_SIZING_RISK_SYNC_REQUIRED_CODE
            : 'risk_overview_unavailable',
        safeMessage: posSizingSyncRequired
            ? POS_SIZING_RISK_SYNC_REQUIRED_MESSAGE
            : (isSelected
                ? 'Selected portfolio risk is temporarily unavailable.'
                : 'Risk metrics are temporarily unavailable.'),
    }),
    group: withRiskOverviewResource({
        success: false,
        status: posSizingSyncRequired ? 'POSITION_SIZING_SYNC_REQUIRED' : 'RISK_OVERVIEW_UNAVAILABLE',
        ...(posSizingSyncRequired ? { action: 'sync_draft' } : {}),
        message: posSizingSyncRequired
            ? POS_SIZING_RISK_SYNC_REQUIRED_MESSAGE
            : 'Attribute risk metrics are temporarily unavailable.',
        portfolio_risks: {},
        attribute_definitions: [],
        attribute_results: [],
    }, {
        scopeKey,
        generation,
        state: RISK_OVERVIEW_RESOURCE_STATES.ERROR,
        errorCode: posSizingSyncRequired
            ? POS_SIZING_RISK_SYNC_REQUIRED_CODE
            : 'risk_overview_unavailable',
        safeMessage: posSizingSyncRequired
            ? POS_SIZING_RISK_SYNC_REQUIRED_MESSAGE
            : 'Attribute risk metrics are temporarily unavailable.',
    }),
});
